// Single source of truth for Cadence's one-tier monetisation.
// Cadence Pro unlocks all AI features; there is no Stripe — IAP only.
// Keys here MUST stay aligned with APPLE_PRODUCT_MAP in the
// cadenceSubscription Lambda.

export const TIERS = { FREE: 'free', PRO: 'pro' };

export const TIER_CONFIG = {
  [TIERS.FREE]: {
    label: 'Free',
    monthlyPrice: 0,
    yearlyPrice: 0,
    features: ['health_dashboard', 'device_calendar', 'banking'],
  },
  [TIERS.PRO]: {
    label: 'Cadence Pro',
    monthlyPrice: 6.99,
    yearlyPrice: 59.99,
    trialDays: 7,
    features: [
      'health_dashboard', 'device_calendar', 'banking',
      'ai_briefing', 'ai_chat', 'ai_voice', 'memory',
    ],
  },
};

export const APPLE_PRODUCT_IDS = {
  pro_monthly: 'uk.co.wolftechnologies.cadence.pro.monthly',
  pro_yearly: 'uk.co.wolftechnologies.cadence.pro.yearly',
};

// App-managed free trial: new accounts get full access (AI included) for this
// many days from first sign-in, no purchase required. Enforced server-side.
// Keep in sync with the three backend copies: cadenceCore/src/index.js,
// cadenceBrain/src/entitlements.js, cadenceSubscription/src/index.js.
export const TRIAL_DAYS = 14;

// Daily AI fair-use limits shown in the paywall + settings. Mirrors
// DAILY_LIMITS in the cadenceBrain Lambda (which is what actually enforces).
export const AI_DAILY_LIMITS = {
  chatMessages: 40,
  briefings: 3,
};

export function hasFeature(tier, featureId) {
  return (TIER_CONFIG[tier] || TIER_CONFIG[TIERS.FREE]).features.includes(featureId);
}

// Whether the account is inside the app-managed free trial.
export function isPaidPro(status) {
  if (!status || status.plan !== TIERS.PRO) return false;
  if (status.status === 'active') return true;
  if (status.status === 'canceled') {
    if (!status.currentPeriodEnd) return true;
    return new Date(status.currentPeriodEnd) > new Date();
  }
  return false;
}

// Trial info from the /subscription/status payload → { active, daysLeft, endsAt }.
export function getTrialInfo(status) {
  const trial = status?.trial;
  return {
    active: !!trial?.active,
    daysLeft: trial?.daysLeft ?? null,
    endsAt: trial?.endsAt ?? null,
  };
}

// Effective tier from the backend /subscription/status payload. A live Pro
// subscription OR an active app trial both grant PRO (all AI features).
// 'canceled' keeps Pro until the paid period ends; 'expired' does not.
export function getEffectiveTier(status) {
  if (isPaidPro(status)) return TIERS.PRO;
  if (getTrialInfo(status).active) return TIERS.PRO;
  return TIERS.FREE;
}
